'use client';

import { createContext, JSX, type PropsWithChildren, useContext } from 'react';

/**
 * PublicConfigurationContext
 *
 * This file sets up a React Context that makes certain "public configuration values"
 * available to any client component in the app. These values come from server-side
 * configuration (like Helm) and are injected once at the top level.
 *
 * Key idea:
 * - These are **safe, non-sensitive values** that can be used by client-side code.
 * - Examples: Google OAuth client ID, reCAPTCHA site key, and whether reCAPTCHA is disabled.
 * - Because they are public, they are safe to expose in the browser.
 *
 * Why do this with Context?
 * - Context allows us to avoid passing these values through props manually.
 * - Any component deep in the tree can grab these values with a simple hook.
 *
 * When to use this:
 * - Anytime a client-side component needs access to one of these public values
 *   (like initializing Google login or checking if reCAPTCHA is enabled).
 * - When you need to change a client side configuration value at deploy time.
 */
export type PublicConfigurationContext = {
    googleClientId: string;
    recaptchaDisabled: boolean;
    recaptchaSiteKey: string;
    recaptchaV2SiteKey: string;
};

/**
 * We create the Context here with some "safe default values."
 *
 * These defaults are only used at build time or if someone forgets to wrap
 * their components with the `PublicConfigurationProvider`.
 *
 * In a real running app, the provider will always replace these defaults
 * with actual values injected from the server.
 */
const context = createContext<PublicConfigurationContext>({
    recaptchaDisabled: true,
    googleClientId: '',
    recaptchaSiteKey: '',
    recaptchaV2SiteKey: '',
});

/**
 * Custom hook for convenience.
 *
 * Instead of importing `useContext(context)` everywhere, we provide a named hook
 * so developers can just call `usePublicConfigurationContext()`.
 *
 * Example usage inside a component:
 *   const { googleClientId } = usePublicConfigurationContext();
 */
export function usePublicConfigurationContext(): PublicConfigurationContext {
    return useContext(context);
}

/**
 * Provider component.
 *
 * This component should be placed at the top level of the React tree (inside a layout or root).
 * It takes the actual config values (provided by the server) and "provides" them
 * to all components below.
 *
 * How it works:
 * - React Context lets us wrap our app with `<PublicConfigurationProvider ...>`.
 * - Every child can then call `usePublicConfigurationContext()` to access the values.
 * - `recaptchaDisabled` comes in as a string (`"true"` or `"false"`) from Helm,
 *   so we convert it into a real boolean here.
 *
 * Without this provider, components would only see the fallback defaults.
 */
export function PublicConfigurationProvider(props: PropsWithChildren<PublicConfigurationContext>): JSX.Element {
    const { children, googleClientId, recaptchaDisabled, recaptchaSiteKey, recaptchaV2SiteKey } = props;
    return (
        <context.Provider value={{ googleClientId, recaptchaDisabled, recaptchaSiteKey, recaptchaV2SiteKey }}>
            {children}
        </context.Provider>
    );
}
